import { useEffect, useState } from "react";
import { useGetClientsQuery } from "../redux/services/clients/clientsApi";
import InputDropdown from "../shared/InputDropdown";
import { AvatarClient } from "./AvatarClient";
import { useDebounceSearch } from "../hooks";
import { NewClientForm } from "../pages/Clients/NewClientForm";
import { Button } from "../shared";

interface Client {
  _id: string;
  [key: string]: any;
}

interface FormData {
  client?: Client | null;
  newClient?: boolean;
}

interface ClientsDropdownProps {
  form: FormData;
  updateForm: (update: { id: string; value: any }) => void;
  errors?: Record<string, any>;
  onClick: (client: Client) => void;
  onChange?: (client: Client) => void;
  value?: Client;
  hideFooter?: boolean;
}

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

export const ClientsDropdown: React.FC<ClientsDropdownProps> = ({
  form,
  updateForm,
  errors = {},
  onClick,
  onChange,
  value,
  hideFooter,
}) => {
  const { search, handleSearch } = useDebounceSearch();

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const { data: clients } = useGetClientsQuery({
    page: pageIndex + 1,
    limit: pageSize,
    sortBy: "name",
    sortOrder: "asc",
    search: search.useSearch,
    searchTerm: search.term,
  });

  function globalFilter(value: string): void {
    setPagination({ pageIndex: 0, pageSize });
    handleSearch(value);
  }

  const [showForm, setShowForm] = useState<boolean>(Boolean(form?.newClient));

  useEffect(() => {
    setShowForm(Boolean(form?.newClient));
  }, [form?.newClient]);

  function handleFooterButtonClick(): void {
    updateForm({ id: "client", value: {} });
    updateForm({ id: "newClient", value: true });
    setShowForm(true);
  }

  function handleCancel(): void {
    updateForm({ id: "client", value: null });
    updateForm({ id: "newClient", value: false });
    setShowForm(false);
  }

  function handleChange(e: { id: string; value: any }): void {
    const clientDraft = form?.client || {};
    updateForm({
      id: "client",
      value: { ...clientDraft, [e.id]: e.value },
    });
  }

  return (
    <>
      {showForm ? (
        <>
          <NewClientForm
            form={form?.client || {}}
            updateForm={handleChange}
            errors={errors}
          />
          <Button onClick={handleCancel} variant="link">
            <p className="font-bold">Cancel</p>
          </Button>
        </>
      ) : (
        <InputDropdown
          required
          label="Client"
          onChange={(e: string) => globalFilter(e)}
          placeholder="Search clients by name"
          data={clients?.clients || []}
          footerButtonClick={!hideFooter ? handleFooterButtonClick : undefined}
          footerButton={
            !hideFooter ? <p className="font-bold">+ New Client</p> : undefined
          }
          renderItem={({ item, selected, active }) => {
            const client = item as Client;
            return (
              <AvatarClient
                key={client._id}
                onClick={() => onClick(client)}
                selected={selected}
                className={active ? "bg-gray-100" : undefined}
                {...client}
                disableCancel
              />
            );
          }}
        />
      )}
    </>
  );
};
